import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { BookOpen, Clock, Play, Award, CheckCircle2, Users, Star } from "lucide-react";

export default function CourseDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);

  const load = useCallback(async () => {
    const [c, my] = await Promise.all([
      api.get(`/courses/${slug}`),
      api.get(`/my/enrollments`).catch(() => ({ data: [] })),
    ]);
    setCourse(c.data);
    setEnrollment(my.data.find((e) => e.course_slug === slug) || null);
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  if (!course) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const totalLessons = course.modules.reduce((s, m) => s + m.lessons.length, 0);
  const done = new Set(enrollment?.completed_lessons || []);
  const progress = enrollment?.progress || 0;
  // Auto-grant access model: every logged-in student can watch every published course.
  const hasAccess = !!(user && user.role === "student") || !!enrollment;

  return (
    <div className="space-y-8" data-testid="course-detail">
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="relative rounded-3xl overflow-hidden">
            <img src={course.thumbnail} alt={course.title} className="w-full h-80 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-[#050B14]/40 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <span className="rounded-full bg-gold/95 text-[#1E3A8A] text-[10px] font-bold px-2.5 py-1 uppercase tracking-widest">{course.category}</span>
              <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-3">{course.title}</h1>
              <p className="text-white/85 mt-2 max-w-2xl">{course.tagline}</p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-6 text-sm">
            <span className="flex items-center gap-2 text-muted-foreground"><Clock className="h-4 w-4" />{course.duration_hours}h</span>
            <span className="flex items-center gap-2 text-muted-foreground"><BookOpen className="h-4 w-4" />{totalLessons} lessons</span>
            <span className="flex items-center gap-2 text-muted-foreground"><Users className="h-4 w-4" />{course.students_enrolled}+ students</span>
            <span className="flex items-center gap-2 text-gold font-semibold"><Star className="h-4 w-4 fill-gold" />{course.rating}</span>
            <span className="flex items-center gap-2 text-muted-foreground">Difficulty: {course.difficulty}</span>
          </div>

          <div className="mt-8">
            <h2 className="font-display text-2xl font-bold">About this program</h2>
            <p className="text-muted-foreground mt-3 leading-relaxed">{course.description}</p>
          </div>

          <div className="mt-8">
            <h2 className="font-display text-2xl font-bold mb-4">Curriculum</h2>
            <div className="space-y-4">
              {course.modules.map((m) => (
                <div key={m.id} className="rounded-2xl border border-border bg-card overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-border bg-secondary/40">
                    <p className="font-display font-bold">{m.title}</p>
                    <p className="text-xs text-muted-foreground">{m.lessons.length} lessons</p>
                  </div>
                  <ul className="divide-y divide-border">
                    {m.lessons.map((l, i) => {
                      const completed = done.has(l.id);
                      return (
                        <li key={l.id} className="px-5 py-3.5 flex items-center gap-4">
                          <span className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${completed ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400" : "bg-secondary"}`}>
                            {completed ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                          </span>
                          <div className="flex-1">
                            <p className="text-sm font-semibold">{l.title}</p>
                            <p className="text-xs text-muted-foreground">{l.duration_min} min</p>
                          </div>
                          {hasAccess ? (
                            <Link to={`/app/courses/${course.slug}/lesson/${l.id}`}
                                  data-testid={`open-lesson-${l.id}`}
                                  className="text-xs font-semibold text-primary hover:underline flex items-center gap-1">
                              <Play className="h-3 w-3" /> Watch
                            </Link>
                          ) : (
                            <Link to="/login"
                                  className="text-xs font-semibold text-primary hover:underline flex items-center gap-1">
                              <Play className="h-3 w-3" /> Sign in to watch
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Side panel */}
        <div className="lg:sticky lg:top-24 h-fit rounded-3xl bg-card border border-border p-6 space-y-5">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Trainer</p>
            <p className="font-display font-bold text-lg mt-1">{course.trainer}</p>
            <p className="text-xs text-muted-foreground">IIM Certified · Career Coach</p>
          </div>
          {hasAccess ? (
            <>
              <div>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-semibold text-gold">{progress}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
              <Button className="w-full h-11 rounded-xl royal-gradient border-0 text-white btn-lift"
                onClick={() => {
                  const firstIncomplete = course.modules.flatMap(m => m.lessons).find(l => !done.has(l.id));
                  const target = firstIncomplete || course.modules[0].lessons[0];
                  nav(`/app/courses/${course.slug}/lesson/${target.id}`);
                }}
                data-testid="resume-course-btn">
                <Play className="h-4 w-4 mr-2" /> {enrollment && progress > 0 ? "Resume learning" : "Start learning"}
              </Button>
              <Link to={`/app/courses/${course.slug}/quiz`}>
                <Button variant="outline" className="w-full h-11 rounded-xl border-gold text-gold btn-lift">
                  <Award className="h-4 w-4 mr-2" /> Take final quiz
                </Button>
              </Link>
            </>
          ) : (
            <Link to="/login" className="block">
              <Button data-testid="signin-to-access-btn"
                className="w-full h-11 rounded-xl royal-gradient border-0 text-white btn-lift">
                Sign in to access
              </Button>
            </Link>
          )}

          <div className="pt-4 border-t border-border space-y-2 text-sm">
            {[
              "Full lifetime access",
              "Downloadable resources",
              "Verified certificate",
              "Community access",
            ].map((t) => (
              <div key={t} className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-gold" /> {t}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
